import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorInfo, ScheduleMasterItem } from '../../../../api/model';
import { usePostScheduleMastersSave } from '../../../../api/schedules/schedules';
import { useDefaultOptions } from '../../../../hooks/api';
import SpinnerModal from '../../../modules/SpinnerModal';
import { MasterScheduleMainFormModal } from './MasterScheduleMainFormModal';
import { MasterScheduleSubFormModal } from './MasterScheduleSubFormModal';

type FormProps = {
  scheduleMasters: ScheduleMasterItem[];
};
type Props = {
  scheduleMasters: ScheduleMasterItem[];
  refetch: () => void;
};
function MasterSchedule({ scheduleMasters, refetch }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [scheduleMasterItem, setScheduleMasterItem] = useState<ScheduleMasterItem | null>(null);
  const [index, setIndex] = useState<number | null>(null);

  const { isLoading, mutateAsync, isSuccess } = usePostScheduleMastersSave<AxiosError<ErrorInfo>, unknown>(options);

  const { control, register, handleSubmit, reset } = useForm<FormProps>({
    defaultValues: {
      scheduleMasters,
    },
  });

  const { fields, append, update } = useFieldArray({ name: 'scheduleMasters', control });

  const handleSetForm = (values: ScheduleMasterItem) => {
    if (index !== null) {
      update(index, values);
    }
  };

  const closeModal = () => {
    setIndex(null);
    setScheduleMasterItem(null);
    setIsOpen(false);
  };

  const onSubmit: SubmitHandler<FormProps> = (params) => {
    const postScheduleMasterSave = async () => {
      await mutateAsync({
        data: { schedule_master_items: params.scheduleMasters },
      });
    };
    void postScheduleMasterSave();
  };

  useEffect(() => {
    if (isSuccess) {
      refetch();
    }
  }, [isSuccess, refetch]);

  return (
    <section>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="wfr">
          <p className="newadd">
            <span className="modal-open" onClick={() => setIsOpen(true)}>
              <i className="fa-solid fa-circle-plus" />
              {t('サブ日程追加')}
            </span>
          </p>
          <div className="scroll">
            <table className="table53 mb30 schedule-table">
              <tbody>
                <tr>
                  <th rowSpan={2}>{t('削除')}</th>
                  <th rowSpan={2}>{t('分類')}</th>
                  <th rowSpan={2}>{t('工程')}</th>
                  <th rowSpan={2}>{t('マイルストーン')}</th>
                  <th colSpan={8}>{t('表示')}</th>
                  <th colSpan={4}>{t('期間')}</th>
                  <th rowSpan={2}>{t('通知タイミング')}</th>
                  <th rowSpan={2}>{t('通知メッセージ')}</th>
                  <th rowSpan={2}> </th>
                </tr>
                <tr>
                  <th>
                    {t('FMC,NEW')}
                    <br />
                    {t('OM')}
                  </th>
                  <th>
                    {t('FMC,NEW')}
                    <br />
                    {t('dOM')}
                  </th>
                  <th>
                    {t('FMC,NEW')}
                    <br />
                    {t('WebOM')}
                  </th>
                  <th>
                    {t('FMC,NEW')}
                    <br />
                    {t('翻訳')}
                  </th>
                  <th>
                    {t('MMC,YMC')}
                    <br />
                    {t('OM')}
                  </th>
                  <th>
                    {t('MMC,YMC')}
                    <br />
                    {t('dOM')}
                  </th>
                  <th>
                    {t('MMC,YMC')}
                    <br />
                    {t('WebOM')}
                  </th>
                  <th>
                    {t('MMC,YMC')}
                    <br />
                    {t('翻訳')}
                  </th>
                  <th>{t('FMC/KA有')}</th>
                  <th>{t('FMC/KA無')}</th>
                  <th>{t('MMC/KA有')}</th>
                  <th>{t('MMC/KA無')}</th>
                </tr>
                {fields.map((scheduleMaster, i) => (
                  <tr key={`scheduleMaster-${String(i)}`}>
                    <td>
                      <span className="ck-box">
                        {scheduleMaster.master_division_id === 2 && (
                          <>
                            <input type="checkbox" id={String(i)} {...register(`scheduleMasters.${i}.deleted`)} />
                            <label htmlFor={String(i)}>&nbsp;</label>
                          </>
                        )}
                      </span>
                    </td>
                    <td>{scheduleMaster.master_division}</td>
                    <td>{scheduleMaster.process}</td>
                    <td>{scheduleMaster.milestone}</td>
                    <td style={{ textAlign: 'center' }}>{scheduleMaster.display_fmc_new_om === 1 ? '○' : '-'}</td>
                    <td style={{ textAlign: 'center' }}>{scheduleMaster.display_fmc_new_dom === 1 ? '○' : '-'}</td>
                    <td style={{ textAlign: 'center' }}>{scheduleMaster.display_fmc_new_webom === 1 ? '○' : '-'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {scheduleMaster.display_fmc_new_translation === 1 ? '○' : '-'}
                    </td>
                    <td style={{ textAlign: 'center' }}>{scheduleMaster.display_mmc_ymc_om === 1 ? '○' : '-'}</td>
                    <td style={{ textAlign: 'center' }}>{scheduleMaster.display_mmc_ymc_dom === 1 ? '○' : '-'}</td>
                    <td style={{ textAlign: 'center' }}>{scheduleMaster.display_mmc_ymc_webom === 1 ? '○' : '-'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {scheduleMaster.display_mmc_ymc_translation === 1 ? '○' : '-'}
                    </td>
                    <td style={{ textAlign: 'center' }}>{scheduleMaster.period_fmc_with_ka}</td>
                    <td style={{ textAlign: 'center' }}>{scheduleMaster.period_fmc_without_ka}</td>
                    <td style={{ textAlign: 'center' }}>{scheduleMaster.period_mmc_with_ka}</td>
                    <td style={{ textAlign: 'center' }}>{scheduleMaster.period_mmc_without_ka}</td>
                    <td style={{ whiteSpace: 'pre' }}>{scheduleMaster.notification_timing_expression}</td>
                    <td style={{ whiteSpace: 'pre' }}>{scheduleMaster.message}</td>
                    <td>
                      <span
                        className="modal-open"
                        onClick={() => {
                          setIndex(i);
                          setScheduleMasterItem(scheduleMaster);
                          setIsOpen(true);
                        }}
                      >
                        <i className="fa-solid fa-pen-to-square" />
                        {t('編集')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="btnfr btnfr2 mb20">
            <div style={{ width: 'auto' }}>
              <span className="btn2 btnbg-g mr10">
                <Link to={`/${language}/masterslist`}>{t('キャンセル')}</Link>
              </span>

              <span className="mr10">
                <input
                  type="submit"
                  value={t('元に戻す').toString()}
                  className="btn1 btnbg-g"
                  onClick={() => reset()}
                />
              </span>
              <span>
                <input type="submit" value={t('更新').toString()} className="btn1 lsp1" />
              </span>
            </div>
          </div>
        </div>
      </form>

      {isOpen && scheduleMasterItem?.master_division_id === 1 && (
        <MasterScheduleMainFormModal
          scheduleMaster={scheduleMasterItem}
          open={isOpen}
          onClose={() => closeModal()}
          setFormValues={handleSetForm}
        />
      )}
      {isOpen && scheduleMasterItem?.master_division_id === 2 && (
        <MasterScheduleSubFormModal
          scheduleMaster={scheduleMasterItem}
          open={isOpen}
          onClose={() => closeModal()}
          setFormValues={handleSetForm}
        />
      )}
      {isOpen && scheduleMasterItem === null && (
        <MasterScheduleSubFormModal
          open={isOpen}
          onClose={() => closeModal()}
          setFormValues={handleSetForm}
          append={append}
        />
      )}

      {isLoading && <SpinnerModal />}
    </section>
  );
}

export { MasterSchedule };
